import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EmpresaAtivaProvider, useEmpresaAtiva } from './contexts/EmpresaAtivaContext';
import { ToastProvider, useToast } from './components/shared/Toast';
import { ModuleGuard, FeatureGuard } from './components/guards';
import { queryClient } from './queryClient';
const loadingFallback = <div style={{ padding: 16 }}>Carregando...</div>;

const MainLayout = lazy(() => import('./components/layout/MainLayout').then((m) => ({ default: m.MainLayout })));
const Login = lazy(() => import('./pages/auth/Login').then((m) => ({ default: m.Login })));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const RegistrarPage = lazy(() => import('./pages/auth/RegistrarPage'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard').then((m) => ({ default: m.Dashboard })));
const Financeiro = lazy(() => import('./pages/financeiro/Financeiro').then((m) => ({ default: m.Financeiro })));
const FinanceiroDashboard = lazy(() => import('./pages/financeiro/FinanceiroDashboard'));
const DREPage = lazy(() => import('./pages/financeiro/DREPage').then((m) => ({ default: m.DREPage })));
const FluxoCaixaPage = lazy(() => import('./pages/financeiro/FluxoCaixaPage'));
const AnalyticsFinanceiroPage = lazy(() => import('./pages/financeiro/AnalyticsFinanceiroPage'));
const RegrasClassificacaoPage = lazy(() => import('./pages/financeiro/RegrasClassificacaoPage'));
const TransacoesPage = lazy(() => import('./pages/financeiro/TransacoesPage'));
const TransferenciasPage = lazy(() => import('./pages/financeiro/TransferenciasPage'));
const CartoesPage = lazy(() => import('./pages/financeiro/CartoesPage'));
const FaturaItensPage = lazy(() => import('./pages/financeiro/FaturaItensPage'));
const PassivosPage = lazy(() => import('./pages/financeiro/PassivosPage'));
const PagamentosRecorrentesPage = lazy(() => import('./pages/financeiro/PagamentosRecorrentesPage'));
const PagamentosParciaisPage = lazy(() => import('./pages/financeiro/PagamentosParciaisPage'));
const TitulosPagarPage = lazy(() => import('./pages/financeiro/TitulosPagarPage'));
const TitulosReceberPage = lazy(() => import('./pages/financeiro/TitulosReceberPage'));
const OrcamentosPage = lazy(() => import('./pages/orcamentos/OrcamentosPage'));
const ContasReceberRelatoriosPage = lazy(() => import('./pages/financeiro/ContasReceberRelatoriosPage'));
const NotasFiscaisPage = lazy(() => import('./pages/fiscal/NotasFiscaisPage'));
const FornecedoresClientesPage = lazy(() => import('./pages/financeiro/FornecedoresClientesPage'));
const PedidosPage = lazy(() => import('./pages/comercial/PedidosPage'));
const PreFaturasPage = lazy(() => import('./pages/comercial/PreFaturasPage'));
const CentrosCustoPage = lazy(() => import('./pages/financeiro/CentrosCustoPage'));
const CaixaBancosPage = lazy(() => import('./pages/financeiro/CaixaBancosPage'));
const GruposContasPage = lazy(() => import('./pages/financeiro/GruposContasPage'));
const PlanoContasPage = lazy(() => import('./pages/financeiro/PlanoContasPage'));
const FrotasDashboardPage = lazy(() => import('./pages/frotas/DashboardPage'));
const DashboardWorkspacePage = lazy(() => import('./pages/frotas/DashboardWorkspacePage'));
const ManutencaoPage = lazy(() => import('./pages/frotas/ManutencaoPage'));
const AbastecimentoPage = lazy(() => import('./pages/frotas/AbastecimentoPage'));
const ViagensPage = lazy(() => import('./pages/frotas/ViagensPage'));
const PneusPage = lazy(() => import('./pages/frotas/PneusPage'));
const FrotasVeiculosPage = lazy(() => import('./pages/frotas/VeiculosPage'));
const MultasPage = lazy(() => import('./pages/frotas/MultasPage'));
const ChecklistPage = lazy(() => import('./pages/frotas/ChecklistPage'));
const ChecklistExecutePage = lazy(() => import('./pages/frotas/ChecklistExecutePage'));
const ChecklistDetailPage = lazy(() => import('./pages/frotas/ChecklistDetailPage'));
const OrdemServicoPage = lazy(() => import('./pages/frotas/OrdemServicoPage'));
const OrdensCarregamentoPage = lazy(() => import('./pages/frotas/OrdensCarregamentoPage'));
const AdiantamentosPage = lazy(() => import('./pages/frotas/AdiantamentosPage'));
const DevolucoesPage = lazy(() => import('./pages/frotas/DevolucoesPage'));
const ManifestoFretePage = lazy(() => import('./pages/frotas/ManifestoFretePage'));
const CiotFrotaPropriaPage = lazy(() => import('./pages/frotas/CiotFrotaPropriaPage'));
const CiotTerceirosPage = lazy(() => import('./pages/frotas/CiotTerceirosPage'));
const Comercial = lazy(() => import('./pages/comercial/Comercial').then((m) => ({ default: m.Comercial })));
const ComercialDashboardPage = lazy(() => import('./pages/comercial/DashboardPage'));
const CalculadoraFretesPage = lazy(() => import('./pages/comercial/CalculadoraFretesPage'));
const CotacaoEspelhoPage = lazy(() => import('./pages/comercial/CotacaoEspelhoPage'));
const PropostasLicitacaoPage = lazy(() => import('./pages/comercial/PropostasLicitacaoPage'));
const PropostaLicitacaoDetailPage = lazy(() => import('./pages/comercial/PropostaLicitacaoDetailPage'));
const ConfiguracoesSistemaPage = lazy(() => import('./pages/configuracoes/SistemaPage'));
const ConfiguracoesUsuariosPage = lazy(() => import('./pages/configuracoes/UsuariosPage'));
const ConfiguracoesEmpresasPage = lazy(() => import('./pages/configuracoes/EmpresasPage'));
const FiliaisPage = lazy(() => import('./pages/configuracoes/FiliaisPage'));
const GruposPage = lazy(() => import('./pages/configuracoes/GruposPage'));
const AuditLogsPage = lazy(() => import('./pages/configuracoes/AuditLogsPage'));
const ConfiguracaoSefazPage = lazy(() => import('./pages/configuracoes/ConfiguracaoSefazPage'));
const PlanosPage = lazy(() => import('./pages/configuracoes/PlanosPage'));
const PlanoBloqueadoPage = lazy(() => import('./pages/configuracoes/PlanoBloqueadoPage'));
const AjudaPage = lazy(() => import('./pages/configuracoes/AjudaPage'));
const ComprovantesPage = lazy(() => import('./pages/basicolog/ComprovantesPage'));
const MotoristasPage = lazy(() => import('./pages/cadastros/MotoristasPage'));
const VeiculosCadastroPage = lazy(() => import('./pages/cadastros/VeiculosPage'));
const GruposVeiculosPage = lazy(() => import('./pages/cadastros/GruposVeiculosPage'));
const CategoriaVeiculosPage = lazy(() => import('./pages/cadastros/CategoriaVeiculosPage'));
const VeiculosAgregadosPage = lazy(() => import('./pages/cadastros/VeiculosAgregadosPage'));
const FormasPagamentoPage = lazy(() => import('./pages/cadastros/FormasPagamentoPage'));
const BloqueioMovimentacoesPage = lazy(() => import('./pages/cadastros/BloqueioMovimentacoesPage'));
const ConciliacaoWorkspace = lazy(() => import('./pages/tesouraria/ConciliacaoWorkspace'));
const MovimentacaoPage = lazy(() => import('./pages/tesouraria/MovimentacaoPage'));
const RecebimentosPage = lazy(() => import('./pages/tesouraria/RecebimentosPage'));
const IntegracaoBancariaPage = lazy(() => import('./pages/tesouraria/IntegracaoBancariaPage'));
const CtesPage = lazy(() => import('./pages/fiscal/CtesPage'));
const MinutasPage = lazy(() => import('./pages/fiscal/MinutasPage'));
const ContratosPage = lazy(() => import('./pages/fiscal/ContratosPage'));
const ConfiguracoesFiscaisPage = lazy(() => import('./pages/fiscal/ConfiguracoesFiscaisPage'));
const TabelasIcmsPage = lazy(() => import('./pages/fiscal/TabelasIcmsPage'));
const SeguradorasPage = lazy(() => import('./pages/fiscal/SeguradorasPage'));
const DrePage = lazy(() => import('./pages/fiscal/DrePage'));
const CentrosCustoWorkspace = lazy(() => import('./pages/financeiro/CentrosCustoWorkspace'));
const AbastecimentoWorkspace = lazy(() => import('./pages/frotas/AbastecimentoWorkspace'));
const PneusWorkspace = lazy(() => import('./pages/frotas/PneusWorkspace'));
const CombustivelAnalyticsPage = lazy(() => import('./pages/frotas/CombustivelAnalyticsPage'));
const ViagensWorkspace = lazy(() => import('./pages/frotas/ViagensWorkspace'));
const RastreamentoCargasWorkspace = lazy(() => import('./pages/frotas/RastreamentoCargasWorkspace'));
const ExtratosPage = lazy(() => import('./pages/tesouraria/ExtratosPage'));
const FuncionariosListaPage = lazy(() => import('./pages/rh/FuncionariosListaPage'));
const FichaColaboradorPage = lazy(() => import('./pages/rh/FichaColaboradorPage'));
const ControleJornadaPage = lazy(() => import('./pages/rh/ControleJornadaPage'));
const RHDashboardPontoPage = lazy(() => import('./pages/rh/RHDashboardPontoPage'));
const RHFolhaDashboardPage = lazy(() => import('./pages/rh/RHFolhaDashboardPage'));
const HorasExtrasPage = lazy(() => import('./pages/rh/HorasExtrasPage'));
const DsrPage = lazy(() => import('./pages/rh/DsrPage'));
const FolhasPage = lazy(() => import('./pages/rh/FolhasPage'));
const RubricasPage = lazy(() => import('./pages/rh/RubricasPage'));
const VerbasVariaveisPage = lazy(() => import('./pages/rh/VerbasVariaveisPage'));
const RhAdiantamentosPage = lazy(() => import('./pages/rh/AdiantamentosPage'));
const AlertasPage = lazy(() => import('./pages/rh/AlertasPage'));
const RubricasFixasPage = lazy(() => import('./pages/rh/RubricasFixasPage'));
const ContasPagarWorkspace = lazy(() => import('./pages/financeiro/ContasPagarWorkspace'));
const AiAgentsPage = lazy(() => import('./pages/ai-agents/AiAgentsPage'));
const AriamSignaisPage = lazy(() => import('./pages/ariam/AriamSignaisPage'));
const AriamEmpresasPage = lazy(() => import('./pages/ariam/AriamEmpresasPage'));
const CopilotPage = lazy(() => import('./pages/copilot/CopilotPage'));
const FpaPage = lazy(() => import('./pages/ai-agents/FpaPage'));
const FleetHealthPage = lazy(() => import('./pages/ai-agents/FleetHealthPage'));
const FleetCostPage = lazy(() => import('./pages/ai-agents/FleetCostPage'));
const TesourariaAgentePage = lazy(() => import('./pages/tesouraria/TesourariaAgentePage'));
const FiscalCompliancePage = lazy(() => import('./pages/fiscal/FiscalCompliancePage'));
const ExecutiveReportPage = lazy(() => import('./pages/ai-agents/ExecutiveReportPage'));
const EmailInboxPage = lazy(() => import('./pages/ai-agents/EmailInboxPage'));
const EmailAccountsPage = lazy(() => import('./pages/ai-agents/EmailAccountsPage'));
const DiagnosticoPage = lazy(() => import('./pages/ai-agents/DiagnosticoPage'));
const AlertsFeedPage = lazy(() => import('./pages/ai-agents/AlertsFeedPage'));
const MdfePage = lazy(() => import('./pages/fiscal/MdfePage'));
const SuprimentosProdutosPage = lazy(() => import('./pages/suprimentos/ProdutosPage'));
const SuprimentosFamiliasProdutoPage = lazy(() => import('./pages/suprimentos/FamiliasProdutoPage'));
const SuprimentosEstoquePage = lazy(() => import('./pages/suprimentos/EstoquePage'));
const SuprimentosAlmoxarifadosPage = lazy(() => import('./pages/suprimentos/AlmoxarifadosPage'));
const SuprimentosFinalidadesPage = lazy(() => import('./pages/suprimentos/FinalidadesPage'));
const SuprimentosSolicitacoesPage = lazy(() => import('./pages/suprimentos/SolicitacoesPage'));
const SuprimentosCotacoesPage = lazy(() => import('./pages/suprimentos/CotacoesPage'));
const SuprimentosPedidosCompraPage = lazy(() => import('./pages/suprimentos/PedidosCompraPage'));
const SuprimentosRecebimentoPedidosPage = lazy(() => import('./pages/suprimentos/RecebimentoPedidosPage'));

const SessionExpiredHandler = () => {
  const { warning } = useToast();

  useEffect(() => {
    const handler = () => {
      warning(
        'Sessão expirada',
        'Sua sessão expirou. Você será redirecionado para o login.',
        7000
      );
      setTimeout(() => {
        window.location.href = '/login';
      }, 3000);
    };

    window.addEventListener('session-expired', handler);
    return () => window.removeEventListener('session-expired', handler);
  }, [warning]);

  return null;
};

const BackendUnavailableHandler = () => {
  const { warning } = useToast();

  useEffect(() => {
    const handler = () => {
      warning(
        'Servidor em reinício',
        'O backend está indisponível no momento. Aguarde alguns segundos e tente novamente.',
        8000
      );
    };

    window.addEventListener('backend-unavailable', handler);
    return () => window.removeEventListener('backend-unavailable', handler);
  }, [warning]);

  return null;
};

const PLAN_PAGES = ['/plano-bloqueado'];

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  const { isEmpresaContextReady } = useEmpresaAtiva();
  const location = useLocation();

  if (isLoading || !isEmpresaContextReady) return <div>Carregando...</div>;
  if (!user) return <Navigate to="/login" />;

  const isSuperAdmin = (user.role || '').toUpperCase() === 'SUPER_ADMIN';
  const hasPlan = isSuperAdmin || user.tenant_plan?.has_plan_selected !== false;
  const isPendentePagamento = user.tenant_status === 'pendente_pagamento';

  if ((!hasPlan || isPendentePagamento) && !PLAN_PAGES.includes(location.pathname)) {
    return <Navigate to="/plano-bloqueado" replace />;
  }

  return <>{children}</>;
};

const SuperAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  const { isEmpresaContextReady } = useEmpresaAtiva();

  if (isLoading || !isEmpresaContextReady) return <div>Carregando...</div>;
  if (!user) return <Navigate to="/login" />;

  const isSuperAdmin = (user.role || '').toUpperCase() === 'SUPER_ADMIN';
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <EmpresaAtivaProvider>
          <ToastProvider>
            <SessionExpiredHandler />
            <BackendUnavailableHandler />
            <BrowserRouter>
              <Suspense fallback={loadingFallback}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/registrar" element={<RegistrarPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Rotas para janelas destacáveis (workspaces) */}
              <Route
                path="/tesouraria/conciliacao/workspace"
                element={
                  <PrivateRoute>
                    <ModuleGuard module="tesouraria"><ConciliacaoWorkspace /></ModuleGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/centros-custo-workspace"
                element={
                  <PrivateRoute>
                    <ModuleGuard module="financeiro"><CentrosCustoWorkspace /></ModuleGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/frotas/abastecimento/workspace"
                element={
                  <PrivateRoute>
                    <ModuleGuard module="frotas"><AbastecimentoWorkspace /></ModuleGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/frotas/pneus/workspace"
                element={
                  <PrivateRoute>
                    <ModuleGuard module="frotas"><PneusWorkspace /></ModuleGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/frotas/adiantamentos"
                element={
                  <PrivateRoute>
                    <ModuleGuard module="frotas"><AdiantamentosPage /></ModuleGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/frotas/devolucoes"
                element={
                  <PrivateRoute>
                    <ModuleGuard module="frotas"><DevolucoesPage /></ModuleGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/frotas/viagens/workspace"
                element={
                  <PrivateRoute>
                    <ModuleGuard module="frotas"><ViagensWorkspace /></ModuleGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/frotas/viagens/rastreamento"
                element={
                  <PrivateRoute>
                    <ModuleGuard module="frotas"><RastreamentoCargasWorkspace /></ModuleGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/comercial/cotacoes/:id/espelho"
                element={
                  <PrivateRoute>
                    <ModuleGuard module="comercial"><CotacaoEspelhoPage /></ModuleGuard>
                  </PrivateRoute>
                }
              />

              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <MainLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" />} />
                <Route path="dashboard" element={<FeatureGuard feature="dashboard"><Dashboard /></FeatureGuard>} />
                <Route path="planos-cobranca" element={<SuperAdminRoute><PlanosPage /></SuperAdminRoute>} />
                <Route path="plano-bloqueado" element={<PlanoBloqueadoPage />} />
                <Route path="copilot" element={<CopilotPage />} />
                <Route path="ai-agents" element={<AiAgentsPage />} />
                <Route path="ai-agents/fpa" element={<FpaPage />} />
                <Route path="ai-agents/fleet-health" element={<FleetHealthPage />} />
                <Route path="ai-agents/fleet-cost" element={<FleetCostPage />} />
                <Route path="ai-agents/treasury" element={<TesourariaAgentePage />} />
                <Route path="ai-agents/fiscal-compliance" element={<FiscalCompliancePage />} />
                <Route path="ai-agents/executive-report" element={<ExecutiveReportPage />} />
                <Route path="ai-agents/email-inbox" element={<EmailInboxPage />} />
                <Route path="ai-agents/email-accounts" element={<EmailAccountsPage />} />
                <Route path="ai-agents/diagnostico" element={<DiagnosticoPage />} />
                <Route path="ai-agents/alerts" element={<AlertsFeedPage />} />
                <Route path="ariam/sinais" element={<AriamSignaisPage />} />
                <Route path="ariam/empresas" element={<AriamEmpresasPage />} />
                <Route path="modelos-historico" element={<div>Modelos de Histórico - Em desenvolvimento</div>} />
                <Route path="servicos" element={<div>Serviços - Em desenvolvimento</div>} />
                <Route path="fiscal/minutas" element={<Navigate to="/frotas/minutas" replace />} />
                <Route path="fiscal/contratos-frete" element={<Navigate to="/frotas/contratos-frete" replace />} />
                <Route path="produtos" element={<Navigate to="/suprimentos/produtos" replace />} />
                <Route path="suprimentos/produtos" element={<SuprimentosProdutosPage />} />
                <Route path="suprimentos/familias" element={<SuprimentosFamiliasProdutoPage />} />
                <Route path="suprimentos/estoque" element={<SuprimentosEstoquePage />} />
                <Route path="suprimentos/almoxarifados" element={<SuprimentosAlmoxarifadosPage />} />
                <Route path="suprimentos/finalidades" element={<SuprimentosFinalidadesPage />} />
                <Route path="suprimentos/solicitacoes" element={<SuprimentosSolicitacoesPage />} />
                <Route path="suprimentos/cotacoes" element={<SuprimentosCotacoesPage />} />
                <Route path="suprimentos/pedidos" element={<SuprimentosPedidosCompraPage />} />
                <Route path="suprimentos/recebimentos" element={<SuprimentosRecebimentoPedidosPage />} />

                {/* Financeiro */}
                <Route element={<ModuleGuard module="financeiro"><Outlet /></ModuleGuard>}>
                  <Route path="transacoes" element={<TransacoesPage />} />
                  <Route path="transferencias" element={<TransferenciasPage />} />
                  <Route path="cartoes" element={<CartoesPage />} />
                  <Route path="cartoes/fatura/:faturaId" element={<FaturaItensPage />} />
                  <Route path="passivos" element={<PassivosPage />} />
                  <Route path="pagamentos-recorrentes" element={<PagamentosRecorrentesPage />} />
                  <Route path="pagamentos-parciais" element={<PagamentosParciaisPage />} />
                  <Route path="orcamentos" element={<OrcamentosPage />} />
                  <Route path="relatorios" element={<ContasPagarWorkspace />} />
                  <Route path="contas-receber/relatorios" element={<ContasReceberRelatoriosPage />} />
                  <Route path="centros-custo" element={<CentrosCustoPage />} />
                  <Route path="caixa-bancos" element={<CaixaBancosPage />} />
                  <Route path="grupos-contas" element={<GruposContasPage />} />
                  <Route path="plano-contas" element={<PlanoContasPage />} />
                  <Route path="titulos-pagar" element={<TitulosPagarPage />} />
                  <Route path="titulos-receber" element={<TitulosReceberPage />} />
                  <Route path="financeiro/titulos-receber" element={<TitulosReceberPage />} />
                  <Route path="financeiro" element={<Financeiro />} />
                  <Route path="financeiro/dashboard" element={<FinanceiroDashboard />} />
                  <Route path="financeiro/dre" element={<DREPage />} />
                  <Route path="financeiro/fluxo-caixa" element={<FeatureGuard feature="fluxo_caixa"><FluxoCaixaPage /></FeatureGuard>} />
                  <Route path="financeiro/analytics" element={<AnalyticsFinanceiroPage />} />
                  <Route path="financeiro/regras" element={<RegrasClassificacaoPage />} />
                  <Route path="financeiro/transacoes" element={<TransacoesPage />} />
                </Route>

                {/* Fiscal */}
                <Route element={<ModuleGuard module="fiscal"><Outlet /></ModuleGuard>}>
                  <Route path="fiscal/notas-fiscais" element={<NotasFiscaisPage />} />
                  <Route path="fiscal/ctes" element={<CtesPage />} />
                  <Route path="fiscal/dre" element={<DrePage />} />
                  <Route path="fiscal/configuracoes" element={<ConfiguracoesFiscaisPage />} />
                  <Route path="fiscal/tabelas-icms" element={<TabelasIcmsPage />} />
                  <Route path="fiscal/seguradoras" element={<SeguradorasPage />} />
                  <Route path="fiscal/compliance" element={<FiscalCompliancePage />} />
                  <Route path="fiscal/mdfe" element={<MdfePage />} />
                </Route>

                {/* RH */}
                <Route element={<ModuleGuard module="rh"><Outlet /></ModuleGuard>}>
                  <Route path="rh/funcionarios" element={<FuncionariosListaPage />} />
                  <Route path="rh/ponto" element={<RHDashboardPontoPage />} />
                  <Route path="rh/folha/dashboard" element={<RHFolhaDashboardPage />} />
                  <Route path="rh/folha/horas-extras" element={<HorasExtrasPage />} />
                  <Route path="rh/folha/dsr" element={<DsrPage />} />
                  <Route path="rh/funcionarios/:id" element={<FichaColaboradorPage />} />
                  <Route path="rh/funcionarios/:id/jornada" element={<ControleJornadaPage />} />
                  <Route path="rh/folhas" element={<FolhasPage />} />
                  <Route path="rh/rubricas" element={<RubricasPage />} />
                  <Route path="rh/rubricas-fixas" element={<RubricasFixasPage />} />
                  <Route path="rh/verbas-variaveis" element={<VerbasVariaveisPage />} />
                  <Route path="rh/adiantamentos" element={<RhAdiantamentosPage />} />
                  <Route path="rh/alertas" element={<AlertasPage />} />
                </Route>

                {/* Cadastros Básicos */}
                <Route element={<ModuleGuard module="cadastros_basicos"><Outlet /></ModuleGuard>}>
                  <Route path="fornecedores-clientes" element={<FornecedoresClientesPage />} />
                  <Route path="cadastros/parceiros" element={<FornecedoresClientesPage />} />
                  <Route path="cadastros-basicos/comprovantes" element={<ComprovantesPage />} />
                  <Route path="cadastros-basicos/motoristas" element={<MotoristasPage />} />
                  <Route path="cadastros-basicos/veiculos" element={<VeiculosCadastroPage />} />
                  <Route path="cadastros-basicos/grupos-veiculo" element={<GruposVeiculosPage />} />
                  <Route path="cadastros-basicos/categorias-veiculo" element={<CategoriaVeiculosPage />} />
                  <Route path="cadastros-basicos/veiculos-agregados" element={<VeiculosAgregadosPage />} />
                  <Route path="bloqueio-movimentacoes" element={<BloqueioMovimentacoesPage />} />
                  <Route path="formas-pagamento" element={<FormasPagamentoPage />} />
                  <Route path="basicolog/comprovantes" element={<ComprovantesPage />} />
                </Route>

                {/* Tesouraria */}
                <Route element={<ModuleGuard module="tesouraria"><Outlet /></ModuleGuard>}>
                  <Route path="tesouraria/recebimentos" element={<RecebimentosPage />} />
                  <Route path="tesouraria/extratos" element={<ExtratosPage />} />
                  <Route path="tesouraria/conciliacao" element={<ConciliacaoWorkspace />} />
                  <Route path="tesouraria/integracao" element={<IntegracaoBancariaPage />} />
                  <Route path="tesouraria/movimentacao" element={<MovimentacaoPage />} />
                </Route>

                {/* Frotas */}
                <Route element={<ModuleGuard module="frotas"><Outlet /></ModuleGuard>}>
                  <Route path="frotas/dashboard" element={<FrotasDashboardPage />} />
                  <Route path="frotas/dashboard/workspace" element={<DashboardWorkspacePage />} />
                  <Route path="frotas/combustivel-analytics" element={<CombustivelAnalyticsPage />} />
                  <Route path="frotas/manutencao" element={<ManutencaoPage />} />
                  <Route path="frotas/ordens-servico" element={<OrdemServicoPage />} />
                  <Route path="frotas/ordens-carregamento" element={<OrdensCarregamentoPage />} />
                  <Route path="frotas/abastecimento" element={<AbastecimentoPage />} />
                  <Route path="frotas/viagens" element={<ViagensPage />} />
                  <Route path="frotas/minutas" element={<MinutasPage />} />
                  <Route path="frotas/contratos-frete" element={<ContratosPage />} />
                  <Route path="frotas/manifesto-frete" element={<ManifestoFretePage />} />
                  <Route path="frotas/ciot-frota-propria" element={<CiotFrotaPropriaPage />} />
                  <Route path="frotas/ciot-terceiros" element={<CiotTerceirosPage />} />
                  <Route path="frotas/adiantamentos" element={<AdiantamentosPage />} />
                  <Route path="frotas/devolucoes" element={<DevolucoesPage />} />
                  <Route path="frotas/veiculos" element={<FrotasVeiculosPage />} />
                  <Route path="frotas/pneus" element={<PneusPage />} />
                  <Route path="frotas/multas" element={<MultasPage />} />
                  <Route path="frotas/checklist" element={<ChecklistPage />} />
                  <Route path="frotas/checklist/:execucaoId/executar" element={<ChecklistExecutePage />} />
                  <Route path="frotas/checklist/:execucaoId/detalhes" element={<ChecklistDetailPage />} />
                  <Route path="frotas" element={<Navigate to="/frotas/dashboard" replace />} />
                </Route>

                {/* Comercial */}
                <Route element={<ModuleGuard module="comercial"><Outlet /></ModuleGuard>}>
                  <Route path="comercial/dashboard" element={<ComercialDashboardPage />} />
                  <Route path="comercial/pedidos" element={<PedidosPage />} />
                  <Route path="comercial/prefaturas" element={<PreFaturasPage />} />
                  <Route path="comercial/calculadora-fretes" element={<CalculadoraFretesPage />} />
                  <Route path="comercial/licitacoes" element={<PropostasLicitacaoPage />} />
                  <Route path="comercial/licitacoes/:id" element={<PropostaLicitacaoDetailPage />} />
                  <Route path="comercial" element={<Comercial />} />
                </Route>

                {/* Configurações */}
                <Route element={<ModuleGuard module="configuracoes"><Outlet /></ModuleGuard>}>
                  <Route path="configuracoes/sistema" element={<ConfiguracoesSistemaPage />} />
                  <Route path="configuracoes/usuarios" element={<ConfiguracoesUsuariosPage />} />
                  <Route path="configuracoes/filiais" element={<FiliaisPage />} />
                  <Route path="configuracoes/grupos" element={<GruposPage />} />
                  <Route path="configuracoes/empresas" element={<ConfiguracoesEmpresasPage />} />
                  <Route path="configuracoes/planos" element={<PlanosPage />} />
                  <Route path="configuracoes/auditoria" element={<AuditLogsPage />} />
                  <Route path="configuracoes/sefaz" element={<ConfiguracaoSefazPage />} />
                  <Route path="configuracoes/ajuda" element={<AjudaPage />} />
                </Route>
              </Route>
            </Routes>
              </Suspense>
            </BrowserRouter>
          </ToastProvider>
        </EmpresaAtivaProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
